import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, CheckCircle2Icon, CalendarIcon } from 'lucide-react';

// Statisch sonar-raster. Bewust zonder pulserende ringen of zwevende
// deeltjes: het motief blijft herkenbaar, maar de pagina blijft rustig.
function SonarGrid() {
  return <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 700" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" style={{
    opacity: 0.12
  }} aria-hidden="true" data-id="element-202" data-mp="4m8m0bnacpke">
      {[80, 180, 280, 380, 480, 580].map((r, i) => <circle key={i} cx="260" cy="360" r={r} stroke="#FFFFFF" strokeWidth="0.8" fill="none" data-id="element-203" data-mp="sarsclmrys9u" data-mpi={i} />)}
      <line x1="260" y1="0" x2="260" y2="700" stroke="#FFFFFF" strokeWidth="0.5" data-id="element-204" data-mp="3d1a8vl7kyddt" />
      <line x1="0" y1="360" x2="800" y2="360" stroke="#FFFFFF" strokeWidth="0.5" data-id="element-205" data-mp="381nu37c3sp2w" />
      {[-45, -22, 0, 22, 45].map((angle, i) => {
      const rad = angle * Math.PI / 180;
      const x2 = 260 + 700 * Math.cos(rad);
      const y2 = 360 + 700 * Math.sin(rad);
      return <line key={i} x1="260" y1="360" x2={x2} y2={y2} stroke="#FFFFFF" strokeWidth="0.35" data-id="element-206" data-mp="39cpboqkkxd0s" data-mpi={i} />;
    })}
    </svg>;
}
export function Hero() {
  return <section className="relative w-full" style={{
    background: 'linear-gradient(135deg, #007AFF 0%, #1B2B44 100%)'
  }} data-id="element-207" data-mp="359jahxcbqc16">
      <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-208" data-mp="2viy3blil3z6n">
        <SonarGrid data-id="element-209" data-mp="2vjg48vg6z1kw" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-24 md:py-32" data-id="element-210" data-mp="2hj5fbxyfzx2d">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-211" data-mp="2hl9rf36hw2dq">
          <div className="lg:col-span-7" data-id="element-212" data-mp="2lk8vklkejv44">
            {/* Badge */}
            <motion.div className="inline-flex items-center gap-2.5 rounded-full px-4 py-2 mb-8 text-sm font-semibold text-white" style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)'
          }} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-213" data-mp="s2b7vyavsj55">
              Eerst peilen. Dan bouwen.
            </motion.div>

            <motion.h1 className="font-bold text-white tracking-tight mb-7" style={{
            fontSize: 'clamp(2.4rem, 4.4vw, 3.75rem)',
            lineHeight: 1.15
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.05
          }} data-id="element-214" data-mp="110wp9q4m9s4c">
              Weet welke vraag er in jouw markt is, voordat je erin investeert
            </motion.h1>

            <motion.p className="text-white text-opacity-85 leading-relaxed mb-10" style={{
            fontSize: 'clamp(1.05rem, 1.7vw, 1.2rem)',
            maxWidth: 620
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.12
          }} data-id="element-215" data-mp="2b0pnh5iq8yik">
              CorePrint zet negentig dagen advertentiedata in als meetinstrument.
              We tonen aan welke zoekopdrachten in jouw markt daadwerkelijk
              aanvragen opleveren, en pas daarna bouwen we de organische
              structuur op die bewezen vraag. Geen aannames, geen zoekwoorden uit
              een tool, maar cijfers uit jouw eigen markt.
            </motion.p>

            <motion.div className="flex flex-wrap gap-4 mb-12" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.18
          }} data-id="element-216" data-mp="e9tr1roirz2c">
              <a href="#gesprek" className="inline-flex items-center gap-2.5 bg-white text-[#0052CC] font-semibold px-7 py-4 rounded-lg shadow-lg hover:bg-gray-100 transition-colors duration-200" data-id="element-217" data-mp="3rglpfajhmgor">
                <CalendarIcon size={18} data-id="element-218" data-mp="2yy81c2nye92v" />
                Plan een presentatie
              </a>
              <a href="#hoe" className="inline-flex items-center gap-2.5 border-2 border-white text-white font-semibold px-7 py-4 rounded-lg hover:bg-white/10 transition-colors duration-200" data-id="element-219" data-mp="2u06hvgmm87qv">
                Bekijk de aanpak
                <ArrowDownIcon size={16} data-id="element-220" data-mp="zl95obyn4kc2" />
              </a>
            </motion.div>

            <motion.div className="flex flex-wrap items-center gap-x-8 gap-y-3" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} data-id="element-221" data-mp="14j15rt9fvcrc">
              {['Vrijblijvende kennismaking', 'Je blijft eigenaar van je eigen data', 'Rapportage op aanvraagniveau'].map(t => <div key={t} className="flex items-center gap-2.5 text-white text-opacity-70" style={{
              fontSize: '0.92rem'
            }} data-id="element-222" data-mp="7s0c11ik6bdb">
                  <CheckCircle2Icon size={16} data-id="element-223" data-mp="29ir2xkz1d2d0" />
                  {t}
                </div>)}
            </motion.div>
          </div>

          {/* Kerncijfers, in plaats van decoratie */}
          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-224" data-mp="1zsxtoqmd8zbx">
            <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-225" data-mp="1ltodhqy5u57y">
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-6" data-id="element-226" data-mp="1gubmcu09abzk">
                Hoe het traject loopt
              </p>
              <div className="space-y-6" data-id="element-227" data-mp="3g6e5u48vr6d8">
                {[{
                fase: 'Fase 1',
                dagen: 'Dag 1 tot 21',
                tekst: 'Markt in kaart en meetcampagne live'
              }, {
                fase: 'Fase 2',
                dagen: 'Dag 21 tot 75',
                tekst: 'Wekelijks meten en bijsturen op conversie'
              }, {
                fase: 'Fase 3',
                dagen: 'Dag 75 tot 90',
                tekst: 'CorePrint-rapport en bouwplan op bewezen vraag'
              }].map(item => <div key={item.fase} className="flex gap-4" data-id="element-228" data-mp="34sm743sjcou4">
                    <div className="flex-shrink-0 w-16" data-id="element-229" data-mp="39qnqkptviq64">
                      <p className="text-sm font-bold text-[#0052CC]" data-id="element-230" data-mp="39p2wt2fdvb9b">
                        {item.fase}
                      </p>
                    </div>
                    <div className="flex-grow border-l border-gray-200 pl-4" data-id="element-231" data-mp="p0ydoicqdeg2">
                      <p className="text-xs text-[#6B7280] mb-1" data-id="element-232" data-mp="tzjbyc8f9o1v">
                        {item.dagen}
                      </p>
                      <p className="text-[#111827] font-medium leading-snug" data-id="element-233" data-mp="1f2slwmuens90">
                        {item.tekst}
                      </p>
                    </div>
                  </div>)}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-234" data-mp="1ouyadmlg7rjg">
                <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-235" data-mp="1k2exkrul50fp">
                  Na negentig dagen weet je per zoekopdracht wat een aanvraag
                  kost. Dat rapport is van jou, ook als je daarna zelf verder
                  wilt.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}